import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NetsapiensDomain } from '@voice/utils';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { NgForOf, NgIf } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'app-domain-multiple-selector',
  standalone: true,
  imports: [
    Button,
    CheckboxModule,
    NgForOf,
    NgIf,
    PanelModule,
    FormsModule,
    InputTextModule,
  ],
  templateUrl: './domain-multiple-selector.component.html',
  styleUrls: ['./domain-multiple-selector.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DomainMultipleSelectorComponent {
  @Input() domains: Partial<NetsapiensDomain>[] = [];

  @Input() twoColumnMin = 20;

  @Input() label!: string;
  @Input() buttonLabel!: string;
  @Input() panelCollapsed = false;
  @Input() requesting = false;

  selectedDomain?: string[];

  query = '';

  @Output() selectionChanged = new EventEmitter<string[] | null>();

  get filteredDomains(): Partial<NetsapiensDomain>[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.domains ?? [];
    return (this.domains ?? []).filter((d) => {
      const dom = (d.domain ?? '').toString().toLowerCase();
      const desc = (d.description ?? '').toString().toLowerCase();
      return dom.includes(q) || desc.includes(q);
    });
  }

  get isAllFilteredSelected(): boolean {
    if (!this.filteredDomains.length) return false;
    return this.filteredDomains.every((d) =>
      this.selectedDomain?.includes(d.domain ?? '')
    );
  }

  get isSomeFilteredSelected(): boolean {
    if (!this.filteredDomains.length) return false;
    return (
      this.filteredDomains.some((d) =>
        this.selectedDomain?.includes(d.domain ?? '')
      ) && !this.isAllFilteredSelected
    );
  }

  getSelectedFilteredCount(): number {
    return this.filteredDomains.filter((d) =>
      this.selectedDomain?.includes(d.domain ?? '')
    ).length;
  }

  toggleSelectAll(): void {
    if (this.isAllFilteredSelected) {
      // Deselect all filtered domains
      const filteredDomainNames = this.filteredDomains
        .map((d) => d.domain)
        .filter((d): d is string => d !== undefined && d !== null);
      this.selectedDomain = (this.selectedDomain ?? []).filter(
        (d) => !filteredDomainNames.includes(d)
      );
    } else {
      // Select all filtered domains
      const filteredDomainNames = this.filteredDomains
        .map((d) => d.domain)
        .filter((d): d is string => d !== undefined && d !== null);
      const currentSelected = this.selectedDomain ?? [];
      const newSelections = filteredDomainNames.filter(
        (d) => !currentSelected.includes(d)
      );
      this.selectedDomain = [...currentSelected, ...newSelections];
    }
  }

  domainsSelected(): void {
    this.selectionChanged.emit(this.selectedDomain ?? null);
  }

  selectionCleared(): void {
    this.selectedDomain = undefined;
    this.selectionChanged.emit(null);
  }

  trackByDomain = (_: number, d: Partial<NetsapiensDomain>) => d.domain ?? _;
}
